import { useCallback, useEffect, useRef, useState } from 'react'
import { Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, Dices, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { pickRandomWorkerName } from '@/frontend/pages/Dashboard/DigitalWorkers/nameSuggestions'

interface Props {
  currentName: string
  onPick: (name: string) => void
  onTrigger?: () => void
  disabled?: boolean
}

const DICE_ICONS: LucideIcon[] = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6]
const SHUFFLE_TICKS = 8
const TICK_BASE_MS = 55
const TICK_GROWTH_MS = 22

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function pickDifferentDiceIndex(previous: number): number {
  let next = Math.floor(Math.random() * DICE_ICONS.length)
  if (next === previous) next = (next + 1) % DICE_ICONS.length
  return next
}

export default function NameShuffleButton({ currentName, onPick, onTrigger, disabled }: Props) {
  const [rolling, setRolling] = useState(false)
  const [diceIndex, setDiceIndex] = useState<number | null>(null)
  const timeoutsRef = useRef<number[]>([])
  const diceIndexRef = useRef<number>(-1)
  const lastImmediatePickRef = useRef<string | null>(null)

  const clearTimeouts = useCallback(() => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id))
    timeoutsRef.current = []
  }, [])

  useEffect(() => () => clearTimeouts(), [clearTimeouts])

  const handleClick = useCallback(() => {
    if (rolling || disabled) return

    onTrigger?.()

    if (prefersReducedMotion()) {
      const finalName = pickRandomWorkerName({
        exclude: [currentName.trim(), lastImmediatePickRef.current || ''].filter(Boolean),
      })
      lastImmediatePickRef.current = finalName
      onPick(finalName)
      return
    }

    setRolling(true)
    diceIndexRef.current = -1

    const firstName = pickRandomWorkerName({
      exclude: [currentName.trim(), lastImmediatePickRef.current || ''].filter(Boolean),
    })
    lastImmediatePickRef.current = firstName
    onPick(firstName)
    const firstDice = pickDifferentDiceIndex(diceIndexRef.current)
    diceIndexRef.current = firstDice
    setDiceIndex(firstDice)

    let excludeName = firstName
    let accumulated = TICK_BASE_MS

    for (let tick = 1; tick < SHUFFLE_TICKS; tick += 1) {
      const delay = accumulated
      const isFinal = tick === SHUFFLE_TICKS - 1
      const timeoutId = window.setTimeout(() => {
        const nextName = pickRandomWorkerName({ exclude: excludeName })
        excludeName = nextName
        onPick(nextName)
        const nextDice = pickDifferentDiceIndex(diceIndexRef.current)
        diceIndexRef.current = nextDice
        setDiceIndex(nextDice)
        if (isFinal) {
          const resetId = window.setTimeout(() => {
            setRolling(false)
            setDiceIndex(null)
          }, 260)
          timeoutsRef.current.push(resetId)
        }
      }, delay)
      timeoutsRef.current.push(timeoutId)
      accumulated += TICK_BASE_MS + tick * TICK_GROWTH_MS
    }
  }, [currentName, disabled, onPick, onTrigger, rolling])

  const ActiveDice = diceIndex !== null ? DICE_ICONS[diceIndex] : null

  return (
    <Button
      id="worker-name-shuffle"
      type="button"
      variant="outline"
      onClick={handleClick}
      disabled={disabled || rolling}
      className={cn(
        'group relative overflow-hidden transition-all duration-200 sm:shrink-0',
        'hover:-translate-y-[1px] hover:shadow-md',
        rolling && 'ring-2 ring-violet-500/40',
      )}
      aria-label="Sortear nome"
    >
      <span className="relative inline-flex h-4 w-4 items-center justify-center">
        {ActiveDice ? (
          <ActiveDice
            key={diceIndex}
            className={cn(
              'absolute inset-0 h-4 w-4 text-violet-500 animate-dice-tumble',
              'drop-shadow-[0_0_6px_rgba(139,92,246,0.6)]',
            )}
          />
        ) : (
          <Dices
            className={cn(
              'h-4 w-4 transition-transform duration-300',
              'group-hover:rotate-12 group-hover:text-violet-500',
            )}
          />
        )}
      </span>
      Sortear
    </Button>
  )
}
